import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Target,
  Plus,
  Trash2,
  Star,
  Globe,
  Utensils,
  Trophy,
  ArrowLeft,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const GOAL_TYPES = [
  { value: "visits", label: "Restaurant Besuche", icon: Utensils, description: "Anzahl Restaurants besuchen" },
  { value: "stars", label: "3-Sterne Restaurants", icon: Star, description: "Drei-Sterne Restaurants besuchen" },
  { value: "countries", label: "Länder entdecken", icon: Globe, description: "Neue Länder kulinarisch entdecken" },
];

const Goals = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newGoal, setNewGoal] = useState({
    goal_type: "visits",
    target_value: 10,
    year: new Date().getFullYear(),
    description: ""
  });

  const fetchGoals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/goals?year=${new Date().getFullYear()}`, { withCredentials: true });
      setGoals(response.data);
    } catch (error) {
      console.error("Error fetching goals:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const handleCreateGoal = async () => {
    try {
      await axios.post(`${API}/api/goals`, newGoal, { withCredentials: true });
      toast.success("Ziel erstellt!");
      setShowAddDialog(false);
      fetchGoals();
      setNewGoal({ goal_type: "visits", target_value: 10, year: new Date().getFullYear(), description: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/api/goals/${goalId}`, { withCredentials: true });
      toast.success("Ziel gelöscht");
      fetchGoals();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const getGoalIcon = (type) => {
    const goalType = GOAL_TYPES.find(g => g.value === type);
    return goalType ? goalType.icon : Target;
  };

  const getGoalLabel = (type) => {
    const goalType = GOAL_TYPES.find(g => g.value === type);
    return goalType ? goalType.label : type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Target className="w-6 h-6 text-primary" />
                <h1 className="text-xl font-light tracking-wide">Meine Ziele {new Date().getFullYear()}</h1>
              </div>
            </div>
            
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="w-4 h-4" />
                  Neues Ziel
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Neues Ziel setzen
                  </DialogTitle>
                  <DialogDescription>
                    Setzen Sie sich ein kulinarisches Ziel für dieses Jahr
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Ziel-Typ</Label>
                    <Select value={newGoal.goal_type} onValueChange={(v) => setNewGoal({...newGoal, goal_type: v})}>
                      <SelectTrigger className="mt-1 border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GOAL_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="w-4 h-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Zielwert</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newGoal.target_value}
                      onChange={(e) => setNewGoal({...newGoal, target_value: parseInt(e.target.value) || 1})}
                      className="mt-1 border-border"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {newGoal.goal_type === "visits" && `${newGoal.target_value} Restaurants besuchen`}
                      {newGoal.goal_type === "stars" && `${newGoal.target_value} Drei-Sterne Restaurants`}
                      {newGoal.goal_type === "countries" && `${newGoal.target_value} verschiedene Länder`}
                    </p>
                  </div>
                  
                  <div>
                    <Label>Beschreibung (optional)</Label>
                    <Input
                      value={newGoal.description}
                      onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                      placeholder="z.B. Mein Frankreich-Jahr"
                      className="mt-1 border-border"
                    />
                  </div>
                  
                  <Button onClick={handleCreateGoal} className="w-full">
                    Ziel erstellen
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        {goals.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-12 text-center">
              <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Noch keine Ziele gesetzt</h3>
              <p className="text-muted-foreground mb-6">
                Setzen Sie sich kulinarische Ziele für {new Date().getFullYear()}!
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Erstes Ziel erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {goals.map((goal) => {
              const GoalIcon = getGoalIcon(goal.goal_type);
              const progress = goal.progress || { current: 0, target: goal.target_value, percentage: 0, completed: false };
              
              return (
                <Card 
                  key={goal.goal_id} 
                  className={`bg-card/50 border-border transition-all ${progress.completed ? 'ring-2 ring-primary/50' : ''}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${progress.completed ? 'bg-primary/20' : 'bg-muted/40'}`}>
                          <GoalIcon className={`w-5 h-5 ${progress.completed ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <CardTitle className="text-base font-medium">
                            {getGoalLabel(goal.goal_type)}
                          </CardTitle>
                          {goal.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{goal.description}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteGoal(goal.goal_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-3xl font-light">{progress.current}</span>
                          <span className="text-muted-foreground text-sm"> / {progress.target}</span>
                        </div>
                        {progress.completed && (
                          <div className="flex items-center gap-1 text-primary">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="text-sm font-medium">Erreicht!</span>
                          </div>
                        )}
                      </div>
                      
                      <Progress value={progress.percentage} className="h-2" />
                      
                      <p className="text-xs text-muted-foreground">
                        {progress.completed 
                          ? "Herzlichen Glückwunsch! Ziel erreicht!" 
                          : `Noch ${progress.target - progress.current} bis zum Ziel`
                        }
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        
        {/* Quick Stats */}
        {goals.length > 0 && (
          <div className="mt-8 pt-8 border-t border-border">
            <h2 className="text-lg font-light mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              Fortschritt Übersicht
            </h2>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
              <Card className="bg-card/30 border-border">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-light">{goals.length}</p>
                  <p className="text-xs text-muted-foreground">Gesetzte Ziele</p>
                </CardContent>
              </Card>
              <Card className="bg-card/30 border-border">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-light text-primary">
                    {goals.filter(g => g.progress?.completed).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Erreicht</p>
                </CardContent>
              </Card>
              <Card className="bg-card/30 border-border">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-light">
                    {Math.round(goals.reduce((acc, g) => acc + (g.progress?.percentage || 0), 0) / goals.length)}%
                  </p>
                  <p className="text-xs text-muted-foreground">Durchschnitt</p>
                </CardContent>
              </Card>
              <Card className="bg-card/30 border-border">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-light">
                    {goals.filter(g => !g.progress?.completed).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Offen</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Goals;
